import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Modal from "../../components/common/Modal";
import Button from "../../components/form/Button";
import Input from "../../components/form/Input";
import TextArea from "../../components/form/TextArea";
import {
  useCreateAccountTagMutation,
  useUpdateAccountTagMutation,
} from "../../redux/services/accountTags";

type AccountTagModalProps = {
  accountId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialName?: string;
  initialDescription?: string;
  tagId?: string;
};

const AccountTagModal: React.FC<AccountTagModalProps> = ({
  accountId,
  isOpen,
  onClose,
  onSuccess,
  initialName = "",
  initialDescription = "",
  tagId,
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [errors, setErrors] = useState<{
    name?: string;
    description?: string;
    general?: string;
  }>({});

  const [createTag, { isLoading: isCreating }] = useCreateAccountTagMutation();
  const [updateTag, { isLoading: isUpdating }] = useUpdateAccountTagMutation();

  const isLoading = isCreating || isUpdating;
  const isEditing = Boolean(tagId);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setDescription(initialDescription);
      setErrors({});
    }
  }, [initialName, initialDescription, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = "Tag name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Tag name must be at least 2 characters";
    }

    if (description.trim().length > 200) {
      newErrors.description = "Description must be 200 characters or less";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    try {
      if (isEditing) {
        await updateTag({
          accountId,
          accountTagId: tagId!,
          name: trimmedName,
          description: trimmedDescription,
        }).unwrap();
        toast.success("Tag updated successfully");
      } else {
        await createTag({
          accountId,
          name: trimmedName,
          description: trimmedDescription,
        }).unwrap();
        toast.success("Tag created successfully");
      }

      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.error("Tag operation failed:", err);

      const detail = err?.data?.detail || err?.data?.message || err?.error;

      if (typeof detail === "string") {
        if (
          detail.toLowerCase().includes("already exists") ||
          detail.toLowerCase().includes("duplicate")
        ) {
          setErrors({ name: detail });
        } else {
          setErrors({ general: detail });
        }
      } else {
        setErrors({
          general: `Failed to ${isEditing ? "update" : "create"} tag. Please try again.`,
        });
      }
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  const clearFieldError = (field: keyof typeof errors) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      width={450}
      maxWidth={500}
      background="bg-white"
    >
      <form
        onSubmit={handleFormSubmit}
        className="p-6 space-y-5"
      >
        <h2 className="text-xl font-semibold text-gray-900">
          {isEditing ? "Edit Tag" : "Create New Tag"}
        </h2>

        {errors.general && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Input
              id="tag-name"
              name="tag-name"
              label="Tag Name"
              required
              placeholder="e.g., News, Product Guide, Documentation"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearFieldError("name");
              }}
              errorMessage={errors.name}
              errorPosition="bottom-left"
              maxLength={30}
              disabled={isLoading}
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500 text-right">
              {name.length}/30
            </p>
          </div>

          <div>
            <TextArea
              id="tag-description"
              name="tag-description"
              label="Description"
              placeholder="Optional"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                clearFieldError("description");
              }}
              errorMessage={errors.description}
              errorPosition="bottom-left"
              maxLength={200}
              disabled={isLoading}
              maxHeight={240}
            />
            <p
              className={`mt-1 text-xs text-right ${
                description.length > 180 ? "text-red-500" : "text-gray-500"
              }`}
            >
              {description.length}/200
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="transparent"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant={!name.trim() ? "disabled" : "primary"}
            loading={isLoading}
            onClick={() => {}}
          >
            {isEditing ? "Update Tag" : "Create Tag"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AccountTagModal;
